import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, FileText } from 'lucide-react';
import { z } from 'zod';
import { CrudDrawer } from '@/shared/modals/CrudDrawer';
import { Form, TextField, SelectField, CheckboxField, FormActions } from '@/components/form';
import { listSeries, createSeriesApi, updateSeriesApi, type Series } from '../series/series.api';

const TYPE_OPTIONS = [
  { value: 'FACTURA', label: 'Factura (F)' },
  { value: 'BOLETA', label: 'Boleta (B)' },
  { value: 'NOTA_CREDITO', label: 'Nota de crédito' },
  { value: 'NOTA_DEBITO', label: 'Nota de débito' },
];

const TYPE_LABEL: Record<Series['documentType'], string> = {
  FACTURA: 'Factura',
  BOLETA: 'Boleta',
  NOTA_CREDITO: 'Nota de crédito',
  NOTA_DEBITO: 'Nota de débito',
};

const schema = z.object({
  documentType: z.enum(['FACTURA', 'BOLETA', 'NOTA_CREDITO', 'NOTA_DEBITO']),
  code: z.string().regex(/^[A-Z0-9]{4}$/, 'Debe ser 4 caracteres alfanuméricos en mayúscula'),
  isDefault: z.boolean(),
});
type FormValues = z.infer<typeof schema>;

export function SeriesTab(): JSX.Element {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['series'], queryFn: listSeries });
  const [drawerOpen, setDrawerOpen] = useState(false);

  const createMut = useMutation({
    mutationFn: createSeriesApi,
    onSuccess: () => {
      toast.success('Serie creada');
      qc.invalidateQueries({ queryKey: ['series'] });
      setDrawerOpen(false);
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const toggleDefault = useMutation({
    mutationFn: (s: Series) => updateSeriesApi(s.id, { isDefault: true }),
    onSuccess: () => {
      toast.success('Serie predeterminada actualizada');
      qc.invalidateQueries({ queryKey: ['series'] });
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const toggleActive = useMutation({
    mutationFn: (s: Series) => updateSeriesApi(s.id, { active: !s.active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['series'] }),
  });

  const rows = query.data ?? [];

  return (
    <div className="cx">
      <div className="card" style={{ maxWidth: 720 }}>
        <div className="card-head">
          <div className="card-title">Series de comprobantes</div>
          <button className="btn btn-primary btn-sm" onClick={() => setDrawerOpen(true)}>
            <Plus size={15} />
            Nueva serie
          </button>
        </div>

        {query.isLoading ? (
          <div style={{ padding: '8px 0' }}>
            {Array.from({ length: 4 }).map((_, r) => (
              <div key={r} className="row gap16" style={{ padding: '13px 16px', borderBottom: '1px solid #F1F5F9' }}>
                {Array.from({ length: 4 }).map((_, c) => (
                  <div key={c} className="sk" style={{ height: 12, flex: 1, opacity: 1 - r * 0.12 }} />
                ))}
              </div>
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="empty">
            <div className="empty-ico">
              <FileText size={28} />
            </div>
            <div>
              <div className="t-h2" style={{ marginBottom: 4 }}>
                Sin series
              </div>
              <div className="muted">Crea series como F001 (facturas) o B001 (boletas).</div>
            </div>
            <button className="btn btn-primary" onClick={() => setDrawerOpen(true)}>
              <Plus size={17} />
              Nueva serie
            </button>
          </div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Serie</th>
                  <th className="right">Próximo correlativo</th>
                  <th>Predeterminada</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id}>
                    <td className="strong">{TYPE_LABEL[s.documentType]}</td>
                    <td className="tnum strong">{s.code}</td>
                    <td className="right tnum">{String(s.nextNumber).padStart(8, '0')}</td>
                    <td>
                      {s.isDefault ? (
                        <span className="badge badge-yellow">
                          <span className="bdot" />
                          Predeterminada
                        </span>
                      ) : (
                        <button className="btn btn-ghost btn-sm" onClick={() => toggleDefault.mutate(s)}>
                          Hacer predeterminada
                        </button>
                      )}
                    </td>
                    <td>
                      <button onClick={() => toggleActive.mutate(s)} style={{ border: 'none', background: 'transparent', padding: 0 }} title="Cambiar estado">
                        <span className={`badge badge-${s.active ? 'green' : 'gray'}`}>
                          <span className="bdot" />
                          {s.active ? 'Activa' : 'Inactiva'}
                        </span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CrudDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        title="Nueva serie"
        description="Las series de factura empiezan con F, las de boleta con B. Use 4 caracteres."
      >
        <Form<FormValues>
          schema={schema}
          defaultValues={{ documentType: 'FACTURA', code: '', isDefault: false }}
          onSubmit={(v) => createMut.mutate(v)}
          className="space-y-4"
        >
          <SelectField<FormValues> name="documentType" label="Tipo" options={TYPE_OPTIONS} required />
          <TextField<FormValues> name="code" label="Código (4 chars)" required />
          <CheckboxField<FormValues> name="isDefault" label="Marcar como predeterminada" />
          <FormActions submitLabel="Crear" isSubmitting={createMut.isPending} onCancel={() => setDrawerOpen(false)} />
        </Form>
      </CrudDrawer>
    </div>
  );
}

function extractError(err: unknown): string {
  return (err as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Error';
}
